<script>
import findIndex from 'lodash/findIndex';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import ExpenseModel from '@models/Expense';
import ExpenseForm from '@views/reports/expense-form';
import ExpenseModal from '@views/reports/edit-expense';
import FileDownloadLink from '@components/FileDownloadLink';
import utility from '@mixins/utility';

export default {
  components: {
    FontAwesomeIcon,
    ExpenseForm,
    ExpenseModal,
    FileDownloadLink,
  },
  mixins: [utility],
  props: {
    collapsed: {
      default: false,
      type: Boolean,
      required: false,
    },
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    report: {
      required: true,
    },
    expenses: {
      default: () => [],
      type: Array,
    },
  },
  data() {
    return {
      selectedExpense: null,
      fields: [
        {
          key: 'id',
          label: this.$t('table_labels.id'),
          sortable: true,
        },
        {
          key: 'date',
          label: this.$t('fields.date'),
          sortable: true,
          formatter: this.timeFormatter,
        },
        {
          key: 'cost_type',
          label: this.$t('fields.cost_type'),
          sortable: false,
        },
        {
          key: 'amount',
          label: this.$t('table_labels.amount'),
          sortable: true,
        },
        {
          key: 'description',
          label: this.$t('fields.description'),
          sortable: true,
        },
        {
          key: 'paid',
          label: this.$t('fields.paid'),
          sortable: true,
        },
        {
          key: 'scan',
          label: this.$t('fields.upload'),
          sortable: false,
          class: 'text-left',
        },
        {
          key: 'export',
          label: this.$t('fields.export'),
          sortable: false,
        },
      ],
    };
  },
  mounted() {
    if (!this.report.locked && !this.report.completed) {
      this.fields.push({
        key: 'actions',
        label: this.$t('table_labels.actions'),
        sortable: false,
      });
    }
  },
  methods: {
    submit(expense) {
      this.$emit('submitted', expense);
    },
    update(expense) {
      this.selectedExpense = null;
      const key = findIndex(this.expenses, { id: expense.id });
      this.$emit('updated', expense, key);
    },
    async deleteExpense(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.expense') }))) {
        this.$wait.start(`expense${id}`);
        const key = findIndex(this.expenses, { id });
        const report = new ReportModel({ id: this.report.id });
        const expense = new ExpenseModel({ id });
        await expense.for(report).delete();
        this.$emit('deleted', id, key);
        this.$wait.end(`expense${id}`);
      }
    },
    checkboxChanged(id, item) {
      this.$nextTick(() => {
        this.$wait.start(`statusLoader${id}`);
        const report = new ReportModel({ id: this.report.id });

        const expense = new ExpenseModel({
          id: `status/${id}`,
          export: item.export,
        });

        expense
          .for(report)
          .save()
          .then(() => {
            this.$wait.end(`statusLoader${id}`);
          });
      });
    },
    timeFormatter(value) {
      return value ? DateTime.fromSQL(value).toFormat('dd.LL.yyyy') : null;
    },
    numberFormatter(value) {
      // @TODO Try to fix globally this dot/comma issue from the backend
      const price = value.toString().replace(',', '.');
      return '€ ' + new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(price);
    },
    exportPDF() {
      this.$wait.start(`downloading${this.report.id }-export_pdf`);
      this.$http
        .get(`reports/export_expenses_pdf/${this.report.id }`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${this.report.id }-export_pdf`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const link = document.createElement('a');
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Report #${this.report.id} expenses.pdf`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${this.report.id}-export_pdf`));
    },

    uploadTimeFormatter(value) {
      return value ? DateTime.fromISO(value).toFormat('dd.LL.yyyy HH:mm') : null;
    },

    // uploadToNextcloud() {
    //   this.$wait.start(`uploading${this.report.id}-nextcloud`);
      
    //   this.$http
    //     .post(`reports/nextcloud-upload/${this.report.id}`)
    //     .then((response) => {
    //       this.$set(this.report, 'expenses_uploaded_at', response.data.uploaded_at);
    //     })
    //     .catch()
    //     .finally(() => {
    //       this.$wait.end(`uploading${this.report.id}-nextcloud`);
    //     });
    // },    
  },
};
</script>

<template>
  <div>
    <b-collapse id="expenses-collapse" v-model="collapsed" class="mt-2">
      <ExpenseForm v-if="!report.locked && !report.completed" ref="expenseForm" :loaded-id="loadedId" :report="report" @submitted="submit" /><br />
    </b-collapse>
    <small v-if="expenses.length" class="table-responsive">
      <b-table stacked="lg" striped hover :items="expenses" :fields="fields" small class="no-min-width">
        <template v-slot:cell(cost_type)="row">
          {{ row.item.cost_type.name }}
        </template>
        <template v-slot:cell(date)="row">
          <div v-if="row.item.date" class="no-wrap">{{ timeFormatter(row.item.date) }}</div>
          <div v-else>-</div>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <FileDownloadLink
              v-if="row.item.scan"
              :file="row.item.scan"
              :removable="false"
              :hide-text="true"
              btn-class="btn mr-2 btn-outline-secondary btn-sm"
            >
              <FontAwesomeIcon :icon="['fas', 'sticky-note']" style="margin-top: 0.35rem!important;"/>
            </FileDownloadLink>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="selectedExpense = row.item.id">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-danger" @click="deleteExpense(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(amount)="row">
          <div>{{ numberFormatter(row.item.amount) }}</div>
        </template>
        <template v-slot:cell(scan)="row">
          <FontAwesomeIcon v-if="row.item.scan != undefined" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(paid)="row">
          <FontAwesomeIcon v-if="row.item.paid" class="text-success" :icon="['fas', 'check-circle']" />
          <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
        </template>
        <template v-slot:cell(export)="row">
          <div v-if="report.locked || report.completed">
            <FontAwesomeIcon v-if="row.item.export" class="text-success" :icon="['fas', 'check-circle']" />
          </div>
          <b-form-checkbox v-else v-model="row.item.export" :value="1" :unchecked-value="0" @change="checkboxChanged(row.item.id, row.item)"> </b-form-checkbox>
        </template>
      </b-table>
    </small>

    <small v-if="expenses.length" class="mt-2 text-muted">
      <div>{{ $t('messages.before_address') }}</div>
    </small>
    <b-btn v-if="expenses.length" class="mt-3 mr-2" variant="outline-dark" @click="exportPDF">
      {{ $t('actions.download') }}
    </b-btn>
    <!-- <b-btn 
        v-if="expenses.length" 
        class="mt-3"
        variant="outline-dark"
        :disabled="!!report.expenses_uploaded_at || $wait.is('uploading' + report.id + '-nextcloud')" 
        @click="uploadToNextcloud"
      >
      <span v-if="report.expenses_uploaded_at">
        Uploaded at {{ uploadTimeFormatter(report.expenses_uploaded_at) }}
      </span>
      <span v-else>
        {{ $wait.is(`uploading${report.id}-nextcloud`) ? $t('infos.uploading') : $t('actions.upload') }}
      </span>
    </b-btn> -->

    <ExpenseModal
      v-if="!report.locked && !report.completed"
      v-model="selectedExpense"
      :loaded-id="selectedExpense"
      :report="report"
      @updated="update"
      @hidden="selectedExpense = null"
    />
  </div>
</template>
